import type {ReactElement} from 'react';
import React from 'react';
import {FOLD_TYPES, FoldType} from '@PosterWhiteboard/poster/fold.class';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {CustomListItem} from '@Components/contextual-menu/components/custom-list-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useAppSelector} from '@/hooks';
import styles from './page-folds-option.module.scss';

export function PageFoldsOption(): ReactElement<any> | null {
  const foldType = useAppSelector((state) => {
    return state.posterEditor.posterObject?.fold.type;
  });
  if (foldType === undefined) {
    return null;
  }

  const getDisplayNameForBackgroundDropdownItem = (dropdownItem: FoldType): string => {
    switch (dropdownItem) {
      case FoldType.NONE:
        return window.i18next.t('pmwjs_none');
      case FoldType.BI_FOLD_HORIZONTAL:
        return window.i18next.t('pmwjs_bi_fold_horizontal');
      case FoldType.BI_FOLD_VERTICAL:
        return window.i18next.t('pmwjs_bi_fold_vertical');
      case FoldType.FOUR_PANEL_HORIZONTAL:
        return window.i18next.t('pmwjs_four_panel_horizontal');
      case FoldType.FOUR_PANEL_VERTICAL:
        return window.i18next.t('pmwjs_four_panel_vertical');
      case FoldType.TRI_FOLD_HORIZONTAL:
        return window.i18next.t('pmwjs_tri_fold_horizontal');
      case FoldType.TRI_FOLD_VERTICAL:
        return window.i18next.t('pmwjs_tri_fold_vertical');
      default:
        throw new Error(`Unhandled page fold dropdown item`);
    }
  };

  const getIconFromType = (id: FoldType): string => {
    switch (id) {
      case FoldType.NONE:
        return 'icon-ban';
      case FoldType.BI_FOLD_HORIZONTAL:
        return 'icon-bi-fold-h';
      case FoldType.BI_FOLD_VERTICAL:
        return 'icon-bi-fold-v';
      case FoldType.FOUR_PANEL_HORIZONTAL:
        return 'icon-quad-fold-h';
      case FoldType.FOUR_PANEL_VERTICAL:
        return 'icon-quad-fold-v';
      case FoldType.TRI_FOLD_HORIZONTAL:
        return 'icon-tri-fold-h';
      case FoldType.TRI_FOLD_VERTICAL:
        return 'icon-tri-fold-v';
      default:
        throw new Error(`Unhandled page fold dropdown item`);
    }
  };

  const getListItems = (id: FoldType): ReactElement<any> => {
    return (
      <CustomListItem
        id={id}
        key={id}
        svgOrIcon={<Icon icon={getIconFromType(id)} size={IconSize.SIZE_ICON_20} className={foldType === id ? 'content-primary' : ''} />}
        text={getDisplayNameForBackgroundDropdownItem(id)}
        isSelected={foldType === id}
        onClick={(): void => {
          onClick(id);
        }}
      />
    );
  };

  const getCustomPopup = (): ReactElement<any> => {
    return (
      <ul className={`spacing-m-0 ${styles.popup}`}>
        {FOLD_TYPES.map((id: FoldType) => {
          return getListItems(id);
        })}
      </ul>
    );
  };

  const onClick = (type: FoldType): void => {
    if (foldType === type) {
      return;
    }

    void window.posterEditor?.whiteboard?.updateFromObject(
      {
        fold: {
          type,
        },
      },
      {
        undoable: false,
      }
    );
  };

  return (
    <PosterEditingBaseDropdownOption
      id="page-folds-option"
      label={window.i18next.t('pmwjs_folds')}
      customPopup={getCustomPopup()}
      position={DROPDOWN_POSITION.FIXED}
      dropdownSelectorText={getDisplayNameForBackgroundDropdownItem(foldType)}
      selectorIcon={getIconFromType(foldType)}
      hasDefaultSpacing
    />
  );
}
